import React from 'react';
import { Navigate } from 'react-router-dom';
import ProtectedRoute from '../components/rbac/ProtectedRoute';
import { PERMISSIONS } from '../constants/permissions';

const AppLayout = React.lazy(() => import('../components/layout/AppLayout'));
const HomePage = React.lazy(() => import('../pages/home/HomePage'));
const LoginPage = React.lazy(() => import('../pages/auth/LoginPage'));
const NotFoundPage = React.lazy(() => import('../pages/misc/NotFoundPage'));
const ComingSoonPage = React.lazy(() => import('../pages/misc/ComingSoonPage'));
const ProjectsPage = React.lazy(() => import('../pages/projects/ProjectsPage'));
const ProgressPage = React.lazy(() => import('../pages/progress/ProgressPage'));
const ProjectDetailPage = React.lazy(() => import('../pages/projects/ProjectDetailPage'));
const UsersPage = React.lazy(() => import('../pages/users/UsersPage'));
const RolesPage = React.lazy(() => import('../pages/rbac/RolesPage'));
const DocumentProcessingPage = React.lazy(() => import('../pages/documents/DocumentProcessingPage'));
const DocumentCheckPage = React.lazy(() => import('../pages/documents/DocumentCheckPage'));
const DataEntryPage = React.lazy(() => import('../pages/documents/./DocumentInputPage'));
const VerifyOtpPage = React.lazy(() => import('../pages/auth/VerifyOtpPage'));
const DocumentScanReviewPage = React.lazy(() => import('../pages/documents/DocumentScanReviewPage'));
const DocumentInputReviewPage = React.lazy(() => import('../pages/documents/DocumentInputReviewPage'));
const DocumentSignAndPackPage = React.lazy(() => import('../pages/documents/DocumentSignAndPackPage'));
const DocumentScanPage = React.lazy(() => import('../pages/documents/DocumentScanPage'));
const InputtedDataPage = React.lazy(() => import('../pages/documents/InputtedDataPage'));
const FolderInputPage = React.lazy(() => import('../pages/folders/FolderInputPage'));
const FolderListPage = React.lazy(() => import('../pages/folders/FolderListPage'));
import ToolsPage from "../pages/misc/ToolsPage";

export interface AppRoute {
  path?: string;
  index?: boolean;
  element: React.ReactNode;
  children?: AppRoute[];
}

export const publicRoutes: AppRoute[] = [
  {
    path: '/login',
    element: <LoginPage />
  },
  {
    path: "/verify-otp",
    element: <VerifyOtpPage />
  }
];

export const protectedRoutes: AppRoute[] = [
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/home" replace /> },
      { path: "home", element: <HomePage /> },
      {
        path: "projects",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.PROJECTS_READ}>
            <ProjectsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "projects/:id",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.PROJECTS_READ}>
            <ProjectDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "projects/progress",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.PROJECTS_READ}>
            <ProgressPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "users",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.USERS_MANAGE}>
            <UsersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "roles",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.RBAC_READ}>
            <RolesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "process/scan",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.DOCUMENT_SCAN}>
            <DocumentScanPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "process/review-scan",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.DOCUMENT_REVIEW_SCAN}>
            <DocumentScanReviewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "process/input",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.DOCUMENT_INPUT}>
            <DataEntryPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "process/input-folder",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.FOLDER_INPUT}>
            <FolderInputPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "process/review-input",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.DOCUMENT_REVIEW_INPUT}>
            <DocumentInputReviewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "process/sign",
        element: (
          <ProtectedRoute requiredPermissions={PERMISSIONS.DOCUMENT_SIGN}>
            <DocumentSignAndPackPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "folders",
        element: (
          <ProtectedRoute>
            <FolderListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "documents",
        element: <DocumentProcessingPage />,
      },
      {
        path: "documents/metadata",
        element: <InputtedDataPage />,
      },
      {
        path: "documents/:id/check",
        element: <DocumentCheckPage />,
      },
      {
        path: "/misc/tools",
        element: <ToolsPage />,
      },
    ],
  },
];

export const fallbackRoutes: AppRoute[] = [
  {
    path: '*',
    element: <NotFoundPage />
  }
];

export const allRoutes: AppRoute[] = [...publicRoutes, ...protectedRoutes, ...fallbackRoutes];
